import { useState, memo, useCallback, useMemo } from "react";
import { useForm, RegisterOptions } from "react-hook-form";
import { BaseButton } from "@/components/buttons/BaseButton";
import { ImageUploader } from "@/components/uploader/ImageUploader";
import { NicheInputField } from "@/components/popups/NicheInputField";
import { showNotification } from "@/components/alerts/Notifications";
import "./NicheForm.scss";

export interface NicheFields {
  id: "name" | "description" | "rules";
  label: string;
  placeholder: string;
  validation: RegisterOptions;
  type?: string;
  rows?: number;
  disabled?: boolean;
  defaultValue: string;
}

export enum NicheFieldsNames {
  name="name",
  description="description",
  rules="rules"
}

interface INicheFormProps {
  ticketId: string | null;
  onSubmit: (data: INicheForm) => void;
  handleTicketId: (ticketId: string) => void;
  fields: { [key in NicheFieldsNames]: NicheFields };
  btnTitle: string;
  preview?: string | null;
}

export interface INicheForm {
  name: string;
  description: string;
  rules: string;
}

export const NicheForm = memo((props: INicheFormProps) => {
  const {
    onSubmit,
    handleTicketId,
    ticketId,
    fields,
    btnTitle,
    preview
  } = props;

  const [imgLoading, setImgLoading] = useState<number>(0);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting }
  } = useForm<INicheForm>({
    mode: "all",
    defaultValues: {
      name: fields[NicheFieldsNames.name].defaultValue,
      description: fields[NicheFieldsNames.description].defaultValue,
      rules: fields[NicheFieldsNames.rules].defaultValue
    }
  });

  const handleImageError = useCallback((message: string) => {
    handleTicketId("");
    showNotification({
      title: message,
      variant: "error"
    });
  }, [handleTicketId]);

  const handleImageLoad = (percent: number) => {
    setImgLoading(percent);
  };

  const isButtonDisabled = useMemo(() => {
    return !isValid || isSubmitting || (preview && imgLoading > 0) || (!preview && (imgLoading > 0 || !ticketId));
  }, [isValid, isSubmitting, preview, imgLoading, ticketId]);

  return (
    <form className="NicheForm" onSubmit={handleSubmit(onSubmit)}>
      <div className="NicheImage">
        <ImageUploader
          preview={preview || undefined}
          title="Choose an image"
          onReady={handleTicketId}
          onError={handleImageError}
          onProgress={handleImageLoad}
        />
      </div>

      {Object.values(fields).map((fieldOptions) => (
        <NicheInputField
          key={fieldOptions.id}
          errors={errors}
          register={register}
          {...fieldOptions}
        />
      ))}

      <div className="BtnWrap">
        <BaseButton type="submit" disabled={!!isButtonDisabled} aria-label={btnTitle}>
          {isSubmitting ? "Uploading..." : btnTitle}
        </BaseButton>
      </div>
    </form>
  );
});

